import { createFileRoute } from "@tanstack/react-router";

import { applicationListQuerySchema } from "@kansai-ai-hub/schema";
import { ENTERPRISE_VISIBLE_APPLICATION_STATUSES } from "@kansai-ai-hub/types";

import { enterpriseApplicationsQueryOptions } from "@/features/applications/queries";
import { EnterpriseApplicationsPage } from "@/features/enterprise/applications-page";
import { sanitizeEnterpriseStatuses } from "@/features/enterprise/audit-filter";
import { STATIC_LABELS } from "@/lib/nav-registry";

const ENTERPRISE_VISIBLE_SET = new Set<string>(ENTERPRISE_VISIBLE_APPLICATION_STATUSES);

/**
 * 企業画面では AUDIT 系 status (PENDING_AUDIT / AUDIT_BLOCKED) を露出させない。
 *
 * 完全な validateSearch 経由での URL 正規化は TanStack Router の Link 側の型推論
 * (zod default 込み) と衝突するため、ここでは API 入力 (`loaderDeps`) と FE UI
 * (FilterMultiSelect の `value`、`sanitizeEnterpriseStatuses` ヘルパー経由) で
 * 二重 sanitize する設計にしている。
 *
 * - URL `?statuses=PENDING_AUDIT,NEW` → そのまま (Mantine の MultiSelect は data に
 *   無い value を silent ignore するので、UI 上は NEW だけが selected)
 * - loader / API filter → loaderDeps で sanitize された値 (visible only) が渡る
 */
export const Route = createFileRoute("/_authenticated/enterprise/applications/")({
  staticData: { crumb: { label: STATIC_LABELS.enterpriseApplications } },
  validateSearch: applicationListQuerySchema,
  loaderDeps: ({ search }) => ({
    ...search,
    statuses: sanitizeEnterpriseStatuses(search.statuses),
    status:
      search.status && ENTERPRISE_VISIBLE_SET.has(search.status)
        ? search.status
        : undefined,
  }),
  // ロールガードは親 layout (`enterprise/route.tsx`) が ENTERPRISE 限定。
  loader: async ({ context, deps }) => {
    await context.queryClient.ensureQueryData(enterpriseApplicationsQueryOptions(deps));
  },
  component: EnterpriseApplicationsPage,
});
